import React, { useState, useEffect, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { ArrowLeft, Plus, Edit, Trash2, Receipt, Package, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { endgoodCostMasterApiService, EndgoodCostMasterDto } from '../services/endgoodCostMasterApi';
import { endgoodApiService, EndgoodIdNameCategoryDto } from '../services/endgoodApi';
import { formatDate } from '../utils/dateUtils';
import { useNavigate } from 'react-router-dom';
import { useBranch } from '../contexts/BranchContext';

const EndgoodCostPage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedBranch } = useBranch();
  const [endgoods, setEndgoods] = useState<EndgoodIdNameCategoryDto[]>([]);
  const [selectedEndgoodId, setSelectedEndgoodId] = useState<string>('');
  const [costs, setCosts] = useState<EndgoodCostMasterDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingCosts, setLoadingCosts] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<{ endgoodId: string; cost: string; effectiveDate: string }>({
    endgoodId: '',
    cost: '',
    effectiveDate: new Date().toISOString().split('T')[0]
  });

  const loadEndgoods = useCallback(async () => {
    try {
      const res = await endgoodApiService.getEndgoodsIdNameCategory();
      if (res.success && res.data) {
        setEndgoods(Array.isArray(res.data) ? res.data : []);
      }
    } catch {
      toast.error('Failed to load endgoods');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadCosts = useCallback(async () => {
    if (!selectedEndgoodId || selectedBranch == null) {
      setCosts([]);
      return;
    }
    try {
      setLoadingCosts(true);
      const res = await endgoodCostMasterApiService.getByEndgoodId(selectedEndgoodId, selectedBranch);
      if (res.success && res.data) {
        setCosts(Array.isArray(res.data) ? res.data : []);
      } else {
        setCosts([]);
      }
    } catch {
      setCosts([]);
    } finally {
      setLoadingCosts(false);
    }
  }, [selectedEndgoodId, selectedBranch]);

  useEffect(() => {
    loadEndgoods();
  }, [loadEndgoods]);

  useEffect(() => {
    loadCosts();
  }, [loadCosts]);

  const openAddDialog = () => {
    if (selectedBranch == null) {
      toast.error('Please select a branch from the header');
      return;
    }
    setEditingId(null);
    setForm({
      endgoodId: selectedEndgoodId || '',
      cost: '',
      effectiveDate: new Date().toISOString().split('T')[0]
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (selectedBranch == null) {
      toast.error('Please select a branch from the header');
      return;
    }
    const endgoodId = form.endgoodId ? parseInt(form.endgoodId, 10) : 0;
    const cost = parseFloat(form.cost);
    const effectiveDate = form.effectiveDate;
    if (!endgoodId || isNaN(cost) || cost < 0 || !effectiveDate) {
      toast.error('Please select an endgood, enter a valid cost, and effective date.');
      return;
    }
    try {
      setSaving(true);
      if (editingId != null) {
        const res = await endgoodCostMasterApiService.update(
          String(editingId),
          {
            id: editingId,
            endgoodId,
            cost,
            effectiveDate,
          },
          selectedBranch
        );
        if (res.success) {
          setDialogOpen(false);
          loadCosts();
        }
      } else {
        const res = await endgoodCostMasterApiService.create({ endgoodId, cost, effectiveDate }, selectedBranch);
        if (res.success) {
          setDialogOpen(false);
          if (selectedEndgoodId === String(endgoodId)) {
            loadCosts();
          } else {
            setSelectedEndgoodId(String(endgoodId));
          }
        }
      }
    } catch {
      // toasts handled in service
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: EndgoodCostMasterDto) => {
    if (selectedBranch == null) {
      toast.error('Please select a branch from the header');
      return;
    }
    if (!window.confirm(`Delete cost ₹${row.cost} effective ${row.effectiveDate?.split('T')[0]}?`)) return;
    try {
      const res = await endgoodCostMasterApiService.delete(String(row.id), selectedBranch);
      if (res.success) loadCosts();
    } catch {
      // toasts in service
    }
  };

  const selectedEndgoodName = selectedEndgoodId
    ? endgoods.find(e => e.id?.toString() === selectedEndgoodId)?.endgoodName ?? '—'
    : '—';

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="outline" size="sm" onClick={() => navigate('/invoices')} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Invoices
        </Button>
      </div>

      <Card className="border-2 border-primary-200 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100 rounded-t-xl">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-primary-200 flex items-center justify-center">
              <Receipt className="h-6 w-6 text-primary-700" />
            </div>
            <div>
              <CardTitle className="text-xl text-primary-800">Endgood Cost</CardTitle>
              <p className="text-sm text-primary-600">Add and manage cost for endgoods (effective date wise)</p>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid gap-6">
            {selectedBranch == null ? (
              <div className="text-center py-8 text-gray-600 border border-dashed border-amber-200 rounded-xl bg-amber-50/50">
                <p className="font-medium text-amber-900">Select a branch</p>
                <p className="text-sm mt-1">Endgood cost is stored per branch. Choose a branch in the header to load and manage costs.</p>
              </div>
            ) : null}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Endgood</Label>
                <Select
                  value={selectedEndgoodId}
                  onValueChange={setSelectedEndgoodId}
                  disabled={loading || selectedBranch == null}
                >
                  <SelectTrigger className="w-full border border-gray-300 rounded-xl">
                    <SelectValue placeholder="Select endgood to view / add cost" />
                  </SelectTrigger>
                  <SelectContentWithSearch searchPlaceholder="Search endgoods..." debounceMs={300}>
                    {endgoods.map((eg) => (
                      <SelectItem key={eg.id} value={String(eg.id)}>
                        {eg.endgoodName} {eg.categoryName ? `(${eg.categoryName})` : ''}
                      </SelectItem>
                    ))}
                  </SelectContentWithSearch>
                </Select>
              </div>
              <div className="flex items-end">
                <Button
                  onClick={openAddDialog}
                  disabled={loading || selectedBranch == null}
                  className="bg-primary-600 hover:bg-primary-700 text-white gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Add Cost
                </Button>
              </div>
            </div>

            {selectedBranch != null && selectedEndgoodId && (
              <>
                <p className="text-sm text-gray-600">
                  Costs for: <strong>{selectedEndgoodName}</strong> (effective date descending)
                </p>
                {loadingCosts ? (
                  <div className="flex items-center justify-center py-12 gap-2">
                    <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
                    <span>Loading costs...</span>
                  </div>
                ) : costs.length === 0 ? (
                  <div className="text-center py-12 border border-dashed border-gray-300 rounded-xl bg-gray-50">
                    <Package className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p className="text-gray-600">No cost entries for this endgood.</p>
                    <p className="text-sm text-gray-500 mt-1">Click &quot;Add Cost&quot; to add one.</p>
                  </div>
                ) : (
                  <div className="border rounded-xl overflow-hidden">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-primary-50">
                          <TableHead className="font-semibold">Effective Date</TableHead>
                          <TableHead className="font-semibold">Cost (₹)</TableHead>
                          <TableHead className="font-semibold">Created At</TableHead>
                          <TableHead className="font-semibold text-right w-[120px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {costs.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell>{row.effectiveDate ? formatDate(row.effectiveDate) : '—'}</TableCell>
                            <TableCell className="font-medium">₹{Number(row.cost).toFixed(2)}</TableCell>
                            <TableCell className="text-gray-600">
                              {row.createdAt ? formatDate(row.createdAt) : '—'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex justify-end">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleDelete(row)}
                                  className="text-red-600 border-red-200 hover:bg-red-50 gap-1"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                  Delete
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </>
            )}

            {selectedBranch != null && !selectedEndgoodId && !loading && (
              <div className="text-center py-12 border border-dashed border-gray-300 rounded-xl bg-gray-50">
                <Package className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                <p className="text-gray-600">Select an endgood to view or add cost.</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{editingId != null ? 'Edit Endgood Cost' : 'Add Endgood Cost'}</DialogTitle>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label>Endgood</Label>
              <Select
                value={form.endgoodId}
                onValueChange={(v) => setForm((f) => ({ ...f, endgoodId: v }))}
                disabled={!!editingId}
              >
                <SelectTrigger className="w-full border border-gray-300 rounded-xl">
                  <SelectValue placeholder="Select endgood" />
                </SelectTrigger>
                <SelectContentWithSearch searchPlaceholder="Search endgoods..." debounceMs={300}>
                  {endgoods.map((eg) => (
                    <SelectItem key={eg.id} value={String(eg.id)}>
                      {eg.endgoodName} {eg.categoryName ? `(${eg.categoryName})` : ''}
                    </SelectItem>
                  ))}
                </SelectContentWithSearch>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Cost (₹)</Label>
              <Input
                type="number"
                min={0}
                step={0.01}
                value={form.cost}
                onChange={(e) => setForm((f) => ({ ...f, cost: e.target.value }))}
                placeholder="0.00"
                className="border border-gray-300 rounded-xl"
              />
            </div>
            <div className="space-y-2">
              <Label>Effective Date</Label>
              <Input
                type="date"
                value={form.effectiveDate}
                onChange={(e) => setForm((f) => ({ ...f, effectiveDate: e.target.value }))}
                className="border border-gray-300 rounded-xl"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-primary-600 hover:bg-primary-700">
              {saving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                editingId != null ? 'Update' : 'Add Cost'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EndgoodCostPage;
